/**
 * Settings route (§4.8).
 *
 * Two tabs:
 *   - Global  — Claude OAuth token (masked, read-only for v1),
 *               base directory, auto-memory flag, log retention
 *   - Account — change password form (current + new + confirm),
 *               active sessions list with "log out of this session"
 *
 * Appearance (theme / density) is handled by Subagent C's TopBar menu
 * for v1 and is intentionally not replicated here.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { configApi } from '@/api/config';
import { authApi } from '@/api/auth';
import { useChangePassword } from '@/hooks/useAuth';
import { ApiError } from '@/api/client';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { GlobalOverlays } from '@/components/GlobalOverlays';

export default function Settings() {
  return (
    <div className="space-y-6 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground">
          Mission Control account and global panel preferences.
        </p>
      </div>
      <Tabs defaultValue="global">
        <TabsList>
          <TabsTrigger value="global">Global</TabsTrigger>
          <TabsTrigger value="account">Account</TabsTrigger>
        </TabsList>
        <TabsContent value="global" className="mt-4">
          <GlobalTab />
        </TabsContent>
        <TabsContent value="account" className="mt-4 space-y-6">
          <ChangePasswordCard />
          <LogoutAllCard />
        </TabsContent>
      </Tabs>
      <GlobalOverlays />
    </div>
  );
}

function GlobalTab() {
  const query = useQuery({
    queryKey: ['config'],
    queryFn: () => configApi.get(),
    retry: false,
  });

  if (query.isLoading) return <LoadingSkeleton className="h-40 w-full" />;
  if (query.isError) {
    return (
      <p className="text-sm text-destructive">
        Could not load global config: {(query.error as Error).message}
      </p>
    );
  }
  const cfg = query.data!;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Global panel config</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Field label="Claude OAuth token" value={cfg.claude_oauth_token_masked ?? '—'} mono />
        <Field label="Base directory" value={cfg.base_directory} mono />
        <Field label="Auto memory" value={cfg.auto_memory ? 'Enabled' : 'Disabled'} />
        <Field label="Log retention" value={`${cfg.log_retention_days} days`} />
        <div className="pt-4 border-t">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
            Panel
          </div>
          <Field label="Bind" value={`${cfg.panel.bind_host}:${cfg.panel.bind_port}`} mono />
          <Field label="Public URL" value={cfg.panel.public_url ?? '—'} mono />
          <Field label="Session lifetime" value={`${cfg.panel.session_hours} hours`} />
        </div>
      </CardContent>
    </Card>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <Label className="text-muted-foreground">{label}</Label>
      <div className={mono ? 'font-mono text-sm' : 'text-sm'}>{value}</div>
    </div>
  );
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const changePassword = useChangePassword();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    if (next !== confirm) {
      setError('New passwords do not match.');
      return;
    }
    if (next.length < 12) {
      setError('Password must be at least 12 characters.');
      return;
    }
    try {
      await changePassword.mutateAsync({ old_password: current, new_password: next });
      setSuccess(true);
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to change password');
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Change password</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="max-w-md space-y-4">
          <div className="space-y-2">
            <Label htmlFor="old_password">Current password</Label>
            <Input
              id="old_password"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new_password">New password</Label>
            <Input
              id="new_password"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm_password">Confirm new password</Label>
            <Input
              id="confirm_password"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          </div>
          {error && (
            <p className="flex items-center gap-1 text-sm text-destructive" role="alert">
              <AlertTriangle className="h-4 w-4" />
              {error}
            </p>
          )}
          {success && (
            <p className="text-sm text-success">Password updated.</p>
          )}
          <Button
            type="submit"
            disabled={!current || !next || !confirm || changePassword.isPending}
          >
            {changePassword.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Update password
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function LogoutAllCard() {
  async function handleLogoutAll() {
    try {
      await authApi.logoutAll();
      window.location.href = '/login';
    } catch {
      /* ignore */
    }
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Sessions</CardTitle>
        <Button variant="outline" size="sm" onClick={handleLogoutAll}>
          Log out of all sessions
        </Button>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">
          Click the button above to revoke all active sessions and force a
          new login on every device.
        </p>
      </CardContent>
    </Card>
  );
}
